/**
 * CategoricalExplainerHub — pilot landing surface for SSM and categorical-
 * structure explainers.
 *
 * Renders the pilot explainers in a single scrollable page with a sticky,
 * hierarchical sidebar nav for jumping between cards. The categorical
 * structures come first so that clicking the matching SSM cross-link drops
 * the reader into the SSM section with the structural picture already in
 * their head.
 *
 * Progressive-disclosure rule: every card opens at the TL;DR tier; the
 * reader chooses how deep to go. The hub itself adds no extra chrome beyond
 * the section dividers — the cards do all the work.
 *
 * This file is intentionally thin: the per-section components live in
 * `hub/HubSections.tsx`, the hierarchical nav lives in `hub/HubSidebar.tsx`,
 * and both are exported from the hub barrel below so per-section tests can
 * mount `<HubSidebar /> + <SomeSection />` without paying the cost of the
 * full hub.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { FONT, GRAY } from "../design-system/tokens.js";
import { HubSidebar } from "./hub/HubSidebar.js";
import {
  AdjunctionFamilySection,
  CategoricalBuildingBlocksSection,
  CompositionalityPrinciplesSection,
  COMPOSITIONALITY_PRINCIPLE_EXPLAINERS,
  DUAL_PAIR_EXPLAINERS,
  DualPairGuaranteesSection,
  IMMUNITY_CLOSURE_GUARANTEE_EXPLAINERS,
  ImmunityClosureGuaranteesSection,
  LinearCompactSection,
  MASTER_THEOREM_EXPLAINERS,
  MasterTheoremsSection,
  MonadFamilySection,
  NEUTRALIZATION_THEOREM_EXPLAINERS,
  NeutralizationTheoremsSection,
  OperadFamilySection,
  PilotSsmSection,
} from "./hub/HubSections.js";

// Re-export so existing import sites that grab section components from
// `CategoricalExplainerHub.tsx` keep working without a sweep.
export {
  AdjunctionFamilySection,
  CategoricalBuildingBlocksSection,
  COMPOSITIONALITY_PRINCIPLE_EXPLAINERS,
  CompositionalityPrinciplesSection,
  DUAL_PAIR_EXPLAINERS,
  DualPairGuaranteesSection,
  HubSidebar,
  IMMUNITY_CLOSURE_GUARANTEE_EXPLAINERS,
  ImmunityClosureGuaranteesSection,
  LinearCompactSection,
  MASTER_THEOREM_EXPLAINERS,
  MasterTheoremsSection,
  MonadFamilySection,
  NEUTRALIZATION_THEOREM_EXPLAINERS,
  NeutralizationTheoremsSection,
  OperadFamilySection,
  PilotSsmSection,
};

const HUB_ROOT_STYLE: React.CSSProperties = {
  maxWidth: "1100px",
  margin: "0 auto",
  padding: "1.5rem",
  fontFamily: FONT.body,
  color: GRAY[700],
  display: "grid",
  gridTemplateColumns: "minmax(0, 220px) minmax(0, 1fr)",
  gap: "1.5rem",
  alignItems: "start",
};

const HUB_HEADER_STYLE: React.CSSProperties = { marginBottom: "1.5rem" };

const HUB_TITLE_STYLE: React.CSSProperties = {
  fontFamily: FONT.display,
  fontSize: "1.75rem",
  color: GRAY[900],
  margin: "0 0 0.25rem",
};

const HUB_LEAD_STYLE: React.CSSProperties = { margin: 0, color: GRAY[600], maxWidth: "60ch" };

export function CategoricalExplainerHub(): React.JSX.Element {
  return (
    <div style={HUB_ROOT_STYLE}>
      <HubSidebar />
      <main>
        <header style={HUB_HEADER_STYLE}>
          <h1 style={HUB_TITLE_STYLE}>Categorical structures and Structural Safety Mechanisms</h1>
          <p style={HUB_LEAD_STYLE}>
            Progressive-disclosure explainers for the building blocks CSK uses to make vulnerability
            classes structurally inexpressible. Read the TL;DR at the top of any card; click through
            the tiers for intuition, formal definitions, CSK relevance, and references. Each SSM
            card links to the categorical structure it uses — and back.
          </p>
        </header>

        <MasterTheoremsSection />
        <CategoricalBuildingBlocksSection />
        <AdjunctionFamilySection />
        <MonadFamilySection />
        <OperadFamilySection />
        <LinearCompactSection />
        <NeutralizationTheoremsSection />
        <DualPairGuaranteesSection />
        <CompositionalityPrinciplesSection />
        <ImmunityClosureGuaranteesSection />
        <PilotSsmSection />
      </main>
    </div>
  );
}
